import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AddStoreModal = ({ clientId, villageCode, storeGroups, onSave, onClose }) => {
  const [name, setName] = useState('');
  const [type, setType] = useState('retail');
  const [category, setCategory] = useState('');
  const [segment, setSegment] = useState('');
  const [tagsInput, setTagsInput] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [previewId, setPreviewId] = useState('');
  
  // Taxonomy state
  const [taxonomy, setTaxonomy] = useState({ types: [], categories: [], segments: [] });
  const [taxonomyLoading, setTaxonomyLoading] = useState(true);

  // Fetch taxonomy on mount
  useEffect(() => {
    const fetchTaxonomy = async () => {
      try {
        const response = await fetch(`${API_URL}/api/admin/unified-clients/${clientId}/taxonomy`);
        if (response.ok) {
          const data = await response.json();
          setTaxonomy(data);
          // Set default type to 'retail' if available, otherwise first type
          if (data.types.length > 0) {
            setType(data.types.includes('retail') ? 'retail' : data.types[0]);
          }
        }
      } catch (err) {
        console.error('Failed to fetch taxonomy:', err);
      } finally {
        setTaxonomyLoading(false);
      }
    };
    fetchTaxonomy();
  }, [clientId]);

  // Generate preview ID as user types name
  useEffect(() => {
    if (name.trim() && villageCode) {
      const normalized = name.toLowerCase().trim()
        .replace(/&/g, '')
        .replace(/[^a-z0-9\s]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
      setPreviewId(`${villageCode}-${normalized}`);
    } else {
      setPreviewId('');
    }
  }, [name, villageCode]);

  const handleGroupToggle = (groupId) => {
    setSelectedGroups(prev => 
      prev.includes(groupId) 
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name.trim()) {
      setError('Store name is required');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const tags = tagsInput
        .split(',')
        .map(t => t.trim())
        .filter(t => t.length > 0);

      const response = await fetch(
        `${API_URL}/api/admin/unified-clients/${clientId}/stores`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: name.trim(),
            type: type || 'retail',
            category: category || null,
            segment: segment || null,
            tags: tags.length > 0 ? tags : null,
            is_active: isActive,
            group_ids: selectedGroups.length > 0 ? selectedGroups : null
          })
        }
      );

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to add store');
      }

      const result = await response.json();
      onSave(result);
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto"
        data-testid="add-store-modal"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
          <h2 className="text-lg font-semibold text-gray-900">Add New Store</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            data-testid="close-add-store"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Nike, Hugo Boss"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="input-store-name"
              autoFocus
            />
          </div>

          {/* Preview ID */}
          {previewId && (
            <div className="bg-gray-50 rounded-lg p-3">
              <span className="text-sm text-gray-500">Store ID: </span>
              <span className="text-sm font-mono text-gray-700">{previewId}</span>
            </div>
          )}

          {/* Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type
            </label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="input-store-type"
              disabled={taxonomyLoading}
            >
              {taxonomy.types.length > 0 ? (
                taxonomy.types.map(t => (
                  <option key={t} value={t}>{t}</option>
                ))
              ) : (
                <option value="retail">retail</option>
              )}
            </select>
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="input-store-category"
              disabled={taxonomyLoading}
            >
              <option value="">— No category —</option>
              {taxonomy.categories.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          {/* Segment */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Segment
            </label>
            <select
              value={segment}
              onChange={(e) => setSegment(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="input-store-segment"
              disabled={taxonomyLoading}
            >
              <option value="">— No segment —</option>
              {taxonomy.segments.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tags
            </label>
            <input
              type="text"
              value={tagsInput}
              onChange={(e) => setTagsInput(e.target.value)}
              placeholder="flagship, new, seasonal (comma-separated)"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="input-store-tags"
            />
            <p className="text-xs text-gray-500 mt-1">Separate multiple tags with commas</p>
          </div>

          {/* Store Groups */}
          {storeGroups && storeGroups.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Store Groups
              </label>
              <div className="space-y-2 bg-gray-50 rounded-lg p-3">
                {storeGroups.map(group => (
                  <label key={group.id} className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedGroups.includes(group.id)}
                      onChange={() => handleGroupToggle(group.id)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      data-testid={`group-checkbox-${group.id}`}
                    />
                    <span className="text-sm text-gray-700">{group.name}</span>
                    <span className="text-xs text-gray-400">({group.store_ids?.length || 0} stores)</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-1">A store can belong to multiple groups</p>
            </div>
          )}

          {/* Active */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="is_active"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              data-testid="input-store-active"
            />
            <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
              Active
            </label>
          </div>

          {/* Error */}
          {error && (
            <div className="p-3 bg-red-50 text-red-600 text-sm rounded" data-testid="add-store-error">
              {error}
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3 sticky bottom-0 bg-white">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-gray-700 hover:text-gray-900 disabled:opacity-50"
            data-testid="cancel-add-store"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving || !name.trim()}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="submit-add-store"
          >
            {saving ? 'Adding...' : 'Add Store'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddStoreModal;
